import { useState, type FormEvent } from 'react';
import { convertPlanToTrip, type PlanDetail } from '../api/plans';
import { HttpError } from '../api/client';

type Props = {
  plan: PlanDetail;
  onClose: () => void;
  onConverted: (tripId: string) => void;
};

export function ConvertPlanModal({ plan, onClose, onConverted }: Props) {
  const datesFromStages = (() => {
    const datedStages = plan.stages
      .map((s) => s.plannedDate)
      .filter((d): d is string => d !== null);
    if (datedStages.length === 0) return { start: '', end: '' };
    const sorted = [...datedStages].sort();
    return { start: sorted[0]!, end: sorted[sorted.length - 1]! };
  })();

  const [title, setTitle] = useState(plan.title);
  const [description, setDescription] = useState(plan.description ?? '');
  const [startDate, setStartDate] = useState(datesFromStages.start);
  const [endDate, setEndDate] = useState(datesFromStages.end);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const r = await convertPlanToTrip(plan.id, {
        title: title.trim() || undefined,
        description: description.trim() || null,
        startDate: startDate || null,
        endDate: endDate || null,
      });
      onConverted(r.tripId);
    } catch (err) {
      setError(err instanceof HttpError ? err.apiError.message : 'Errore conversione');
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Crea viaggio dal piano</h3>
        </div>
        <p className="muted">
          {plan.stages.length} tappe verranno copiate nel nuovo viaggio. Il piano resterà disponibile.
        </p>
        <form onSubmit={handleSubmit} className="convert-form">
          <label>
            <span>Titolo viaggio *</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={200}
              autoFocus
            />
          </label>
          <label>
            <span>Descrizione</span>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={5000}
            />
          </label>
          <div className="row">
            <label>
              <span>Data inizio</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </label>
            <label>
              <span>Data fine</span>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </label>
          </div>
          {error && <p className="err">{error}</p>}
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
              Annulla
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting || !title.trim()}>
              {submitting ? 'Creazione…' : 'Crea viaggio'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
